import type { ActivityEntry } from '@tallyframe/core';

/** The longest gap between two consecutive `report.updated` entries by the
 *  same actor that still folds them into one run (spec "Timeline"). */
export const FOLD_GAP_MS = 10 * 60 * 1000;

export type TimelineItem =
  | { kind: 'single'; entry: ActivityEntry }
  | { kind: 'group'; entries: ActivityEntry[]; actorKey: string; from: string; to: string };

/** Whether `next` (older, since input is newest-first) continues the run
 *  ending at `prev`: both are `report.updated`, both share a non-null
 *  `actorPrincipalId`, and the gap between them is at most `FOLD_GAP_MS`. A
 *  null `actorPrincipalId` never matches — including another null — since an
 *  unattributed edit is never presumed to be the same person as another. */
function canFold(prev: ActivityEntry, next: ActivityEntry): boolean {
  return (
    prev.action === 'report.updated' &&
    next.action === 'report.updated' &&
    prev.actorPrincipalId !== null &&
    prev.actorPrincipalId === next.actorPrincipalId &&
    Math.abs(new Date(prev.occurredAt).getTime() - new Date(next.occurredAt).getTime()) <= FOLD_GAP_MS
  );
}

/**
 * Folds consecutive `report.updated` entries by the same
 * `actorPrincipalId`, each gap at most `FOLD_GAP_MS`, into one `group` item.
 * Any other entry — a different action, a different actor, a null
 * `actorPrincipalId`, or too large a gap — breaks the run and stands alone
 * as a `single` item. A run of exactly one matching entry is still a
 * `single`, not a one-entry `group` (spec "Timeline").
 *
 * `entries` must be newest first, as the activity feed delivers them; the
 * result preserves that order, and each group's own entries stay
 * newest-first too.
 */
export function foldTimeline(entries: ActivityEntry[]): TimelineItem[] {
  const items: TimelineItem[] = [];
  let run: ActivityEntry[] = [];
  let last: ActivityEntry | null = null;

  const flush = (): void => {
    const [first, ...rest] = run;
    if (!first) {
      return;
    }
    if (rest.length === 0) {
      items.push({ kind: 'single', entry: first });
      return;
    }
    // `canFold` never joins a null `actorPrincipalId`, so a run longer than
    // one entry is guaranteed to share a non-null one.
    const actorKey = first.actorPrincipalId;
    if (actorKey === null) {
      throw new Error('unreachable: a folded run always has a non-null actorPrincipalId');
    }
    items.push({
      kind: 'group',
      entries: run,
      actorKey,
      from: (rest[rest.length - 1] ?? first).occurredAt,
      to: first.occurredAt
    });
  };

  for (const entry of entries) {
    if (last && canFold(last, entry)) {
      run.push(entry);
    } else {
      flush();
      run = [entry];
    }
    last = entry;
  }
  flush();

  return items;
}

/**
 * Merges a newly fetched page of entries into the ones already kept,
 * de-duplicating by `id` (a page boundary can repeat the last entry of the
 * previous page) and returning newest first, matching feed order.
 */
export function mergePages(kept: ActivityEntry[], fresh: ActivityEntry[]): ActivityEntry[] {
  const byId = new Map<number, ActivityEntry>();
  for (const entry of kept) {
    byId.set(entry.id, entry);
  }
  for (const entry of fresh) {
    byId.set(entry.id, entry);
  }
  return Array.from(byId.values()).sort((a, b) => b.id - a.id);
}
